import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Bookmark, Link2, Moon } from "lucide-react";
import { toast } from "sonner";
import { getVideo, addVideo, rateVideo, listFolders, createFolder, saveToFolder } from "../lib/api";
import { embedUrl } from "../lib/youtube";
import { StarRating } from "../components/StarRating";

const LINES = ["NO SIDEBAR.", "NO COMMENTS.", "JUST THE VIDEO."];

const EmptyState = () => (
  <div data-testid="empty-state" className="relative w-full h-[70vh] flex flex-col items-center justify-center text-center overflow-hidden">
    <motion.div
      className="absolute inset-0"
      style={{ background: "radial-gradient(circle at center, #18181b 0%, #000000 70%)" }}
      animate={{ opacity: [0.5, 1, 0.5] }}
      transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
    />
    <div className="relative z-10">
      {LINES.map((line, i) => (
        <div key={line} className="overflow-hidden">
          <motion.p
            initial={{ y: "110%" }}
            animate={{ y: 0 }}
            transition={{ delay: 0.2 + i * 0.15, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="text-4xl md:text-6xl font-heading font-bold text-white tracking-tighter opacity-90"
          >
            {line}
          </motion.p>
        </div>
      ))}
      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.9, duration: 0.8 }}
        className="mt-8 text-zinc-500 font-meta text-xs uppercase tracking-[0.3em]"
      >
        Paste a YouTube, Vimeo, or Dailymotion link above to begin the screening
      </motion.p>
    </div>
  </div>
);

export default function Player({ video, setVideo, theater, setTheater }) {
  const [searchParams] = useSearchParams();
  const v = searchParams.get("v");

  useEffect(() => {
    if (v && (!video || video.video_id !== v)) {
      getVideo(v)
        .then(setVideo)
        .catch(async () => {
          // Not in the archive yet (e.g. clicked from imported watch history) — add then play
          try {
            setVideo(await addVideo(`https://www.youtube.com/watch?v=${v}`));
          } catch {}
        });
    }
  }, [v]);

  useEffect(() => {
    if (!theater) return;
    const onKey = (e) => e.key === "Escape" && setTheater(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [theater, setTheater]);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(`${window.location.origin}/?v=${video.video_id}`);
      toast.success("Screening link copied");
    } catch {
      toast.error("Could not copy the link");
    }
  };

  const rate = async (n) => {
    if (!video) return;
    setVideo({ ...video, rating: n });
    await rateVideo(video.video_id, n).catch(() => {});
  };

  const rawSrc = video ? video.embed_url || embedUrl(video.video_id) : "";
  const embedSrc = rawSrc.replace("__HOST__", window.location.hostname);

  const [saveOpen, setSaveOpen] = useState(false);
  const [folders, setFolders] = useState([]);
  const [newFolder, setNewFolder] = useState("");

  const openSave = async () => {
    setSaveOpen(true);
    const f = await listFolders().catch(() => []);
    setFolders(f);
  };

  const saveTo = async (folderId, name) => {
    try {
      await saveToFolder(folderId, video.video_id);
      toast.success(`Saved to ${name}`);
      setSaveOpen(false);
    } catch {
      toast.error("Could not save this video");
    }
  };

  const createAndSave = async (e) => {
    e.preventDefault();
    if (!newFolder.trim()) return;
    try {
      const f = await createFolder(newFolder.trim());
      await saveToFolder(f.folder_id, video.video_id);
      toast.success(`Saved to ${f.name}`);
      setNewFolder("");
      setSaveOpen(false);
    } catch {
      toast.error("Could not create the folder");
    }
  };

  return (
    <div data-testid="player-page" className="flex-1 w-full max-w-[1920px] mx-auto flex flex-col items-center justify-center p-4 md:p-12">
      <AnimatePresence mode="wait">
        {video ? (
          <motion.div
            key={video.video_id}
            initial={{ opacity: 0, y: 24, scale: 0.985 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, scale: 0.99 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="w-full"
          >
            <div className="w-full aspect-video rounded-xl md:rounded-2xl overflow-hidden border border-white/10 shadow-[0_0_80px_rgba(255,255,255,0.06)] bg-black">
              {video.platform === "file" ? (
                <video
                  data-testid="video-frame"
                  src={video.url}
                  controls
                  autoPlay
                  className="w-full h-full"
                />
              ) : (
                <iframe
                  data-testid="video-frame"
                  src={embedSrc}
                  title={video.title}
                  className="w-full h-full border-none"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              )}
            </div>
            <div className={`mt-6 flex items-center justify-between gap-6 px-1 transition-opacity duration-500 ${theater ? "opacity-0 pointer-events-none" : "opacity-100"}`}>
              <div className="flex flex-col gap-2 min-w-0">
                <h1 data-testid="video-title" className="font-heading text-lg md:text-xl text-white/90 tracking-tight line-clamp-1">
                  {video.title}
                </h1>
                <StarRating testid="player-rating" value={video.rating || 0} onRate={rate} />
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span data-testid="now-playing-label" className="hidden md:inline font-meta text-[10px] uppercase tracking-[0.3em] text-zinc-600 mr-2">
                  Now Screening
                </span>
                <button
                  data-testid="copy-link-button"
                  onClick={copyLink}
                  aria-label="Copy shareable link"
                  title="Copy shareable link"
                  className="w-9 h-9 rounded-full border border-white/10 bg-white/5 text-zinc-400 hover:text-white hover:border-white/30 flex items-center justify-center transition-colors duration-200"
                >
                  <Link2 className="w-4 h-4" />
                </button>
                <div className="relative">
                  <button
                    data-testid="save-button"
                    onClick={openSave}
                    aria-label="Save to folder"
                    title="Save to folder"
                    className="w-9 h-9 rounded-full border border-white/10 bg-white/5 text-zinc-400 hover:text-white hover:border-white/30 flex items-center justify-center transition-colors duration-200"
                  >
                    <Bookmark className="w-4 h-4" />
                  </button>
                  <AnimatePresence>
                    {saveOpen && (
                      <>
                        <div className="fixed inset-0 z-40" onClick={() => setSaveOpen(false)} />
                        <motion.div
                          data-testid="save-popover"
                          initial={{ opacity: 0, y: 6, scale: 0.97 }}
                          animate={{ opacity: 1, y: 0, scale: 1 }}
                          exit={{ opacity: 0, y: 6, scale: 0.97 }}
                          transition={{ duration: 0.2 }}
                          className="absolute right-0 top-12 z-50 w-72 bg-[#0A0A0A] border border-white/10 rounded-2xl p-4 shadow-2xl"
                        >
                          <p className="font-meta text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-3 px-1">Save to folder</p>
                          <div className="max-h-48 overflow-y-auto flex flex-col">
                            {folders.map((f) => (
                              <button
                                key={f.folder_id}
                                data-testid={`save-folder-${f.folder_id}`}
                                onClick={() => saveTo(f.folder_id, f.name)}
                                className="text-left px-3 py-2.5 rounded-lg text-sm text-zinc-300 hover:bg-white/5 hover:text-white transition-colors duration-150 flex items-center justify-between gap-3"
                              >
                                <span className="truncate">{f.name}</span>
                                <span className="font-meta text-[10px] text-zinc-600 shrink-0">{f.count}</span>
                              </button>
                            ))}
                            {folders.length === 0 && (
                              <p className="px-3 py-2 text-sm text-zinc-600">No folders yet</p>
                            )}
                          </div>
                          <form onSubmit={createAndSave} className="mt-3 pt-3 border-t border-white/10 flex gap-2">
                            <input
                              data-testid="popover-new-folder-input"
                              value={newFolder}
                              onChange={(e) => setNewFolder(e.target.value)}
                              placeholder="New folder name…"
                              className="flex-1 min-w-0 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-white/30"
                            />
                            <button
                              data-testid="popover-create-folder-button"
                              type="submit"
                              className="px-3 py-2 rounded-lg bg-white text-black font-meta text-[10px] uppercase tracking-widest shrink-0"
                            >
                              Add
                            </button>
                          </form>
                        </motion.div>
                      </>
                    )}
                  </AnimatePresence>
                </div>
                <button
                  data-testid="theater-toggle-button"
                  onClick={() => setTheater(true)}
                  aria-label="Enter theater mode"
                  title="Theater mode"
                  className="w-9 h-9 rounded-full border border-white/10 bg-white/5 text-zinc-400 hover:text-white hover:border-white/30 flex items-center justify-center transition-colors duration-200"
                >
                  <Moon className="w-4 h-4" />
                </button>
              </div>
            </div>
          </motion.div>
        ) : (
          <EmptyState />
        )}
      </AnimatePresence>
    </div>
  );
}
